import React from "react";
import { Link } from "react-router-dom";
import { SakuraFlower, CherryBranchImg } from "./SakuraDecorations";

export default function Footer() {
  const serviceLinks = [
    { title: "Akupunktur", href: "/akupunktur" },
    { title: "Stress & udbrændthed", href: "/stress" },
    { title: "Led- & muskelsmerter", href: "/led-og-muskelsmerter" },
    { title: "Muskelspændinger", href: "/spaendinger" },
    { title: "Rygsmerter & iskias", href: "/ondt-i-ryggen" },
    { title: "Japansk akupunktur", href: "/japansk-akupunktur" },
    { title: "Migræne & hovedpine", href: "/migraene" },
    { title: "Kæbesmerter & spændinger", href: "/ondt-i-kaeben" },
    { title: "Øre akupunktur", href: "/oere-akupunktur" },
    { title: "Vægtregulering", href: "/vaegtregulering" },
    { title: "Rygestop behandling", href: "/rygestop-behandling" },
  ];

  return (
    <footer className="bg-[#3D2118] text-[#ffe0cf]/90 border-t border-[#593427] relative overflow-hidden">
      {/* Decorative Subtle Background Rings */}
      <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full border-[24px] border-white/[0.03] pointer-events-none" />
      <div className="absolute top-12 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full border border-white/[0.02] pointer-events-none" />

      {/* Decorative Botanical Cherry Blossoms from /design-elements/ - kept only 2 subtle corner accents */}
      {/* Top right blooming branch */}
      <div className="absolute -top-10 -right-8 w-64 sm:w-80 lg:w-[360px] opacity-20 pointer-events-none z-0 select-none">
        <CherryBranchImg index={7} flip={true} rotate={-10} className="w-full h-auto drop-shadow-[0_10px_25px_rgba(0,0,0,0.5)]" />
      </div>

      {/* Bottom left delicate flower branch */}
      <div className="absolute -bottom-12 -left-10 w-56 sm:w-72 lg:w-80 opacity-20 pointer-events-none z-0 select-none">
        <CherryBranchImg index={1} flip={false} rotate={15} className="w-full h-auto drop-shadow-[0_10px_25px_rgba(0,0,0,0.5)]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Col 1: Brand & Presentation */}
          <div className="space-y-4">
            <Link to="/" className="inline-block focus:outline-none">
              <div className="flex items-center">
                <img
                  src="https://assets.auxodev.dk/assets/71339d65-a05d-4c02-a540-2260acdfc245/kita-akupunktur-logo.png"
                  alt="Kita Akupunktur logo - klinik i Risskov"
                  className="h-16 sm:h-20 w-auto bg-white/95 p-2 rounded-2xl object-contain shadow-sm hover:opacity-95 transition-opacity"
                  width="240"
                  height="80"
                />
              </div>
            </Link>
            <p className="text-sm text-[#ffe0cf]/85 leading-relaxed">
              Nænsom nålebehandling og japansk akupunktur i Risskov nær Lystrup, Egå og Aarhus. Individuelt tilpasset behandling med fokus på ro og balance.
            </p>
            <div className="pt-2 flex items-center space-x-3">
              <img
                src="https://assets.auxodev.dk/assets/71339d65-a05d-4c02-a540-2260acdfc245/rab-godkendt-akupunktoer-certifikat.png"
                alt="RAB-godkendt certifikat - Praktiserende Akupunktører"
                className="h-10 w-auto bg-white/95 p-1 rounded-lg object-contain"
                width="120"
                height="40"
              />
              <div className="text-[11px] text-[#ffe0cf]/90 leading-tight">
                <span className="font-semibold text-[#e1ef9f] block">RAB-registreret</span>
                Medlem af Praktiserende Akupunktører
              </div>
            </div>
          </div>

          {/* Col 2: Ydelser */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <h3 className="text-white text-xs font-semibold tracking-wider uppercase font-sans">
                Behandlinger
              </h3>
            </div>
            <ul className="space-y-2.5 text-sm">
              {serviceLinks.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-[#ffe0cf]/80 hover:text-white transition-colors flex items-center group"
                  >
                    <i className="fa-solid fa-angle-right text-xs mr-2 text-[#d74814]" aria-hidden="true" />
                    <span>{item.title}</span>
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/priser"
                  className="text-[#ffe0cf]/80 hover:text-white transition-colors flex items-center group pt-1"
                >
                  <i className="fa-solid fa-angle-right text-xs mr-2 text-[#d74814]" aria-hidden="true" />
                  <span className="font-medium text-[#e1ef9f]">Se priser & tilskud</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 3: Navigation & Info */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <h3 className="text-white text-xs font-semibold tracking-wider uppercase font-sans">
                Hurtige links
              </h3>
            </div>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/" className="text-[#ffe0cf]/80 hover:text-white transition-colors flex items-center">
                  <i className="fa-solid fa-angle-right text-xs mr-2 text-[#d74814]" aria-hidden="true" />
                  <span>Forside</span>
                </Link>
              </li>
              <li>
                <Link to="/om-mig" className="text-[#ffe0cf]/80 hover:text-white transition-colors flex items-center">
                  <i className="fa-solid fa-angle-right text-xs mr-2 text-[#d74814]" aria-hidden="true" />
                  <span>Om mig (Mayumi Kita Thiele)</span>
                </Link>
              </li>
              <li>
                <Link to="/priser" className="text-[#ffe0cf]/80 hover:text-white transition-colors flex items-center">
                  <i className="fa-solid fa-angle-right text-xs mr-2 text-[#d74814]" aria-hidden="true" />
                  <span>Priser & Tilskud</span>
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-[#ffe0cf]/80 hover:text-white transition-colors flex items-center">
                  <i className="fa-solid fa-angle-right text-xs mr-2 text-[#d74814]" aria-hidden="true" />
                  <span>FAQ (Spørgsmål & Svar)</span>
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="text-[#ffe0cf]/80 hover:text-white transition-colors flex items-center">
                  <i className="fa-solid fa-angle-right text-xs mr-2 text-[#d74814]" aria-hidden="true" />
                  <span>Kontakt klinikken</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 4: Kontakt & Tidsbestilling */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <h3 className="text-white text-xs font-semibold tracking-wider uppercase font-sans">
                Kontakt & Klinik
              </h3>
            </div>
            <div className="space-y-3 text-sm text-[#ffe0cf]/90">
              <div className="flex items-start space-x-3">
                <i className="fa-solid fa-user text-[#d74814] mt-1 shrink-0 text-xs" aria-hidden="true" />
                <span>
                  <strong className="text-white font-medium block">
                    Mayumi Kita Thiele
                  </strong>
                  RAB Akupunktør
                </span>
              </div>
              <div className="flex items-start space-x-3">
                <i className="fa-solid fa-location-dot text-[#d74814] mt-1 shrink-0 text-xs" aria-hidden="true" />
                <span>
                  Kantorparken 11<br />
                  8240 Risskov (nær Aarhus)
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-phone text-[#d74814] shrink-0 text-xs" aria-hidden="true" />
                <a
                  href="tel:29387186"
                  className="hover:text-white transition-colors font-medium text-white"
                >
                  (+45) 29 38 71 86
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <i className="fa-solid fa-envelope text-[#d74814] shrink-0 text-xs" aria-hidden="true" />
                <a
                  href="mailto:kontakt@kita-akupunktur.dk"
                  className="hover:text-white transition-colors break-all"
                >
                  kontakt@kita-akupunktur.dk
                </a>
              </div>
              <div className="pt-2">
                <span className="text-xs text-[#ffe0cf]/70 block mb-1">Åbningstider efter aftale:</span>
                <span className="text-xs text-[#593427] bg-[#e1ef9f] font-medium px-2.5 py-1 rounded-md border border-[#e1ef9f]/40 inline-flex items-center">
                  <i className="fa-regular fa-clock mr-1.5 text-[10px]" aria-hidden="true" />
                  Mandag – Fredag
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between text-xs text-[#ffe0cf]/70 gap-4">
          <div className="flex items-center space-x-2">
            <SakuraFlower className="w-3 h-3 text-[#d74814]" />
            <span>
              &copy; {new Date().getFullYear()} Kita Akupunktur. Alle rettigheder forbeholdes.
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <span>Risskov • Aarhus • Lystrup • Egå</span>
            <a
              href="https://www.iubenda.com/privacy-policy/89485652"
              className="iubenda-white iubenda-noiframe iubenda-embed hover:text-white transition-colors"
              title="Privatlivspolitik "
            >
              Privatlivspolitik
            </a>
            <a
              href="https://www.iubenda.com/privacy-policy/89485652/cookie-policy"
              className="iubenda-white iubenda-noiframe iubenda-embed hover:text-white transition-colors"
              title="Cookiepolitik "
            >
              Cookiepolitik
            </a>
            <Link to="/kontakt" className="hover:text-white text-[#e1ef9f] transition-colors">
              Tidsbestilling
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
